// UnitDetailsDrawer — the details/edit panel for an existing unit. Shows the
// editable "{Level} details" card, People at unit, People & status, a no-manager
// note, and the status action bar.
//
// Every write affordance here is permission-gated, and on THREE different verbs:
// adding a child unit is `create`, field/people edits are `edit`, and the
// activate/deactivate/hide/unhide flow is the `hide_deactivate` extra. The verbs
// are read from the registry here rather than drilled in as props — one boolean
// was reasonable, three would not be. Field edits save via
// PATCH /company-structure/update-node/:id; the activate/deactivate/hide/unhide
// flow goes through a ConfirmModal → PATCH update-nodes-status. People add/remove
// is still stubbed (toast) until its route ships.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Button,
  Card,
  Chip,
  ConfirmModal,
  Drawer,
  Dropdown,
  Field,
  Icon,
  Switch,
  type ModalTone,
} from '@/components/common';
import {
  useGetCompanyStructureQuery,
  useGetNodeDetailsQuery,
  useUpdateNodeMutation,
  useUpdateNodesStatusMutation,
  type NodeStatus,
} from '@/api/endpoints/structureApi';
import { useGetUsersSelectionQuery, type UserOption } from '@/api/endpoints/usersApi';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import type { OrgUnit } from '@/mocks/structure';
import useModuleAccess, { useCanHideDeactivateStructureNode } from '@/hooks/useModuleAccess';
import { UnitFields, emptyDraft, type UnitDraft } from './unitForm';
import { hasErrors, validateUnitDraft } from './utils/unitValidation';

interface UnitDetailsDrawerProps {
  open: boolean;
  id: string | undefined;
  onClose: () => void;
  /** Open the create drawer parented to this unit (Add unit header button). */
  onAddChild: (parentId: string) => void;
}

// Status transitions: active→Deactivate, deactivated→Activate/Hide, hidden→Unhide.
type StatusAction = 'deactivate' | 'activate' | 'hide' | 'unhide';
const ACTION_TARGET: Record<StatusAction, NodeStatus> = {
  deactivate: 'deactivated',
  activate: 'active',
  hide: 'hidden',
  unhide: 'deactivated',
};
const ACTION_TONE: Record<StatusAction, ModalTone> = {
  deactivate: 'warning',
  activate: 'success',
  hide: 'warning',
  unhide: 'brand',
};
const ACTION_ICON: Record<StatusAction, string> = {
  deactivate: 'circle-slash',
  activate: 'circle-check',
  hide: 'eye-off',
  unhide: 'eye',
};

// Count of all descendant units under `id` in the cached tree (for the copy).
const countDescendants = (units: OrgUnit[], id: string): number => {
  const find = (list: OrgUnit[]): OrgUnit | undefined => {
    for (const u of list) {
      if (u.id === id) return u;
      const hit = find(u.children ?? []);
      if (hit) return hit;
    }
    return undefined;
  };
  const size = (u: OrgUnit): number => (u.children ?? []).reduce((n, c) => n + 1 + size(c), 0);
  const node = find(units);
  return node ? size(node) : 0;
};

const UnitDetailsDrawer = ({ open, id, onClose, onAddChild }: UnitDetailsDrawerProps) => {
  const { t } = useTranslation();
  const { canCreate, canEdit } = useModuleAccess('company_structure');
  const canHideDeactivate = useCanHideDeactivateStructureNode();
  const levelConfig = useLevelConfig();
  const { data, isLoading, isError } = useGetNodeDetailsQuery(id ?? skipToken);
  const [updateNode, { isLoading: saving }] = useUpdateNodeMutation();
  const [updateStatus, { isLoading: statusSaving }] = useUpdateNodesStatusMutation();
  const { data: roots = [] } = useGetCompanyStructureQuery();
  const { data: userOptions = [], isLoading: usersLoading } = useGetUsersSelectionQuery(undefined, {
    skip: !open,
  });

  const [draft, setDraft] = useState<UnitDraft>(emptyDraft);
  const [initial, setInitial] = useState<UnitDraft>(emptyDraft);
  const [draftPeople, setDraftPeople] = useState<UserOption[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<StatusAction | null>(null);
  // Errors show only after a Save attempt; Discard, a reseed or a successful
  // save clear the flag.
  const [submitted, setSubmitted] = useState(false);

  const subUnits = useMemo(() => (data ? countDescendants(roots, data.id) : 0), [roots, data]);

  // Seed the draft (and the dirty-tracking baseline) when details arrive.
  useEffect(() => {
    if (!data) return;
    const seeded: UnitDraft = {
      name: data.name,
      description: data.description,
      location: data.location,
      googleMapLink: data.googleMapLink,
      coordinates: data.coordinates,
      radius: data.radius,
      parentId: '',
      tagIds: data.tags.map((tag) => tag.id),
    };
    setDraft(seeded);
    setInitial(seeded);
    setDraftPeople(data.people);
    setSubmitted(false);
  }, [data]);

  const patch = (changes: Partial<UnitDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const peopleIds = (list: { id: string }[]) => list.map((p) => p.id).join('|');
  const dirty =
    !!data &&
    (draft.name !== initial.name ||
      draft.description !== initial.description ||
      draft.location !== initial.location ||
      draft.googleMapLink !== initial.googleMapLink ||
      draft.coordinates !== initial.coordinates ||
      draft.radius !== initial.radius ||
      draft.tagIds.join('|') !== initial.tagIds.join('|') ||
      peopleIds(draftPeople) !== peopleIds(data.people));

  const errors = validateUnitDraft(draft);

  const save = async () => {
    if (!data || !dirty) return;
    setSubmitted(true);
    if (hasErrors(errors)) return;
    // People go as a diff against the server's current placement.
    const serverIds = new Set(data.people.map((p) => p.id));
    const draftIds = new Set(draftPeople.map((p) => p.id));
    try {
      await updateNode({
        id: data.id,
        body: {
          name: draft.name.trim(),
          description: draft.description.trim(),
          // Optional location fields → null when empty.
          location: draft.location.trim() || null,
          googleMapLink: draft.googleMapLink.trim() || null,
          coordinates: draft.coordinates.trim() || null,
          radius: draft.radius,
          tags: draft.tagIds,
          // Parent isn't editable here — echo it back unchanged.
          parent: data.parentId,
          addedUsers: [...draftIds].filter((uid) => !serverIds.has(uid)),
          removedUsers: [...serverIds].filter((uid) => !draftIds.has(uid)),
        },
      }).unwrap();
      setSubmitted(false);
      toast.success(t('unit_saved'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Confirmed status change (activate/deactivate/hide/unhide) → bulk-status API.
  const runStatus = async () => {
    if (!data || !confirmAction) return;
    try {
      await updateStatus({ nodeIds: [data.id], status: ACTION_TARGET[confirmAction] }).unwrap();
      toast.success(t('unit_status_updated'));
      setConfirmAction(null);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const level = data ? levelConfig(data.level) : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='unit-drawer--form'
      title={data?.name ?? t('text_loading')}
      header={
        data && level ? (
          <>
            <span
              className={`unit-drawer__tile unit-drawer__tile--lg unit-drawer__tile--${level.tone}`}
            >
              <Icon name={level.icon} size={24} />
            </span>
            <span className='app-drawer__heading'>
              <h2 className='app-drawer__title'>{data.name}</h2>
              <span className='unit-drawer__subrow'>
                <Chip variant='neutral'>{t('structure_level', { n: data.level })}</Chip>
                <span className='app-drawer__subtitle'>{level.name}</span>
              </span>
            </span>
          </>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className='unit-drawer__status'>{t('text_loading')}</div>
      ) : isError || !data || !level ? (
        <div className='unit-drawer__status'>{t('error_generic')}</div>
      ) : (
        <>
          {/* ---- {Level} details ---- */}
          <Card padded={false} className='unit-drawer__card'>
            <div className='unit-drawer__section-head'>
              <h3 className='unit-drawer__card-title'>
                {t('unit_details_card_title', { level: level.name })}
              </h3>
              {/* Adds a NODE, so it is `create` — not `edit`, which is what it
                  rode on before this module was properly gated. */}
              {canCreate && (
                <Button
                  size='sm'
                  variant='secondary'
                  iconStart='plus'
                  onClick={() => onAddChild(data.id)}
                >
                  {t('structure_action_add')}
                </Button>
              )}
            </div>

            <UnitFields
              draft={draft}
              patch={patch}
              disabled={!canEdit}
              errors={submitted ? errors : {}}
              namePlaceholder={t('unit_name_placeholder', { level: level.name })}
              parentField={
                <Field label={t('unit_field_parent')}>
                  <div className='unit-drawer__readonly'>
                    <Icon
                      name={data.parentName ? 'corner-left-up' : 'building-2'}
                      size={15}
                      className='unit-drawer__readonly-icon'
                    />
                    {data.parentName || t('unit_parent_root')}
                  </div>
                </Field>
              }
              tierField={
                <Field label={t('unit_field_tier')}>
                  <div className='unit-drawer__level'>
                    <Chip variant='neutral'>{t('structure_level', { n: data.level })}</Chip>
                    <span className='unit-drawer__level-name'>“{level.name}”</span>
                  </div>
                </Field>
              }
              codeField={
                data.referenceNumber ? (
                  <Field label={t('unit_field_code')}>
                    <span className='unit-drawer__code'>{`ND-${data.referenceNumber}`}</span>
                  </Field>
                ) : undefined
              }
            />
          </Card>

          {/* ---- People at unit ---- */}
          <Card padded={false} className='unit-drawer__card'>
            <div className='unit-drawer__card-head'>
              <h3 className='unit-drawer__card-title'>
                {t('unit_people_title', { level: level.name })}
              </h3>
              <p className='unit-drawer__card-subtitle'>{t('unit_people_subtitle')}</p>
            </div>

            <div className='unit-drawer__section'>
              <div className='unit-drawer__people'>
                {draftPeople.map((person) => (
                  <Chip key={person.id} variant='neutral' iconStart='user'>
                    {person.name || person.id}
                    {canEdit && (
                      <button
                        type='button'
                        className='unit-drawer__tag-remove'
                        aria-label={t('unit_person_remove', { name: person.name || person.id })}
                        onClick={() =>
                          setDraftPeople((list) => list.filter((p) => p.id !== person.id))
                        }
                      >
                        <Icon name='x' size={12} />
                      </button>
                    )}
                  </Chip>
                ))}
              </div>
              {canEdit && (
                <Dropdown
                  open={pickerOpen}
                  onClose={() => setPickerOpen(false)}
                  align='start'
                  width={260}
                  trigger={
                    <button
                      type='button'
                      className='unit-drawer__add-person'
                      aria-expanded={pickerOpen}
                      onClick={() => setPickerOpen((o) => !o)}
                    >
                      <Icon name='user-plus' size={15} />
                      {t('unit_add_person')}
                    </button>
                  }
                >
                  <div className='unit-drawer__picker-list'>
                    {usersLoading ? (
                      <span className='unit-drawer__picker-empty'>{t('text_loading')}</span>
                    ) : (
                      (() => {
                        const placed = new Set(draftPeople.map((p) => p.id));
                        const available = userOptions.filter((u) => !placed.has(u.id));
                        return available.length === 0 ? (
                          <span className='unit-drawer__picker-empty'>
                            {t('unit_people_all_placed')}
                          </span>
                        ) : (
                          available.map((u) => (
                            <button
                              key={u.id}
                              type='button'
                              role='menuitem'
                              className='app-dropdown__item unit-drawer__option'
                              onClick={() => {
                                setDraftPeople((list) => [...list, u]);
                                setPickerOpen(false);
                              }}
                            >
                              <Icon name='user' size={16} className='unit-drawer__option-icon' />
                              <span className='unit-drawer__picker-label'>
                                <span className='unit-drawer__picker-name'>{u.name}</span>
                                {u.email && (
                                  <span className='unit-drawer__picker-sub'>{u.email}</span>
                                )}
                              </span>
                              <Icon name='plus' size={16} className='unit-drawer__option-marker' />
                            </button>
                          ))
                        );
                      })()
                    )}
                  </div>
                </Dropdown>
              )}
            </div>
          </Card>

          {/* ---- People & status ---- */}
          <Card padded={false} className='unit-drawer__card'>
            <div className='unit-drawer__card-head'>
              <h3 className='unit-drawer__card-title'>{t('unit_people_status_title')}</h3>
            </div>

            <div className='unit-drawer__section'>
              <div className='unit-drawer__metric'>
                <span className='unit-drawer__tile unit-drawer__tile--lg unit-drawer__tile--blue'>
                  <Icon name='users-round' size={22} />
                </span>
                <span className='unit-drawer__metric-body'>
                  <span className='unit-drawer__metric-value'>{data.peopleCount}</span>
                  <span className='unit-drawer__metric-text'>
                    {t('unit_people_count', { count: data.peopleCount, name: data.name })}
                  </span>
                </span>
              </div>

              <div className='unit-drawer__status-row'>
                <span className='unit-drawer__status-text'>
                  <span className='unit-drawer__status-label'>
                    {data.status === 'active' ? t('unit_active') : t('unit_inactive')}
                  </span>
                  <span className='unit-drawer__status-hint'>
                    {data.status === 'active' ? t('unit_active_hint') : t('unit_inactive_hint')}
                  </span>
                </span>
                <Switch
                  checked={data.status === 'active'}
                  disabled={!canHideDeactivate || data.status === 'hidden'}
                  aria-label={data.status === 'active' ? t('unit_active') : t('unit_inactive')}
                  onChange={() =>
                    setConfirmAction(data.status === 'active' ? 'deactivate' : 'activate')
                  }
                />
              </div>
            </div>
          </Card>

          {/* ---- no-manager note ---- */}
          <p className='unit-drawer__no-manager'>
            <Icon name='network' size={16} />
            <span>
              <strong>{t('unit_no_manager_title')}</strong> {t('unit_no_manager_text')}
            </span>
          </p>

          {/* ---- action bar ----
              Two unrelated halves: the status transitions ride on
              `hide_deactivate`, the save footer on `edit`. The wrapper renders
              when EITHER is held and each half gates itself — gating the whole
              bar on one verb would either strip a hide_deactivate-only user of
              the status buttons, or leave an edit-only user unable to save. */}
          {(canHideDeactivate || canEdit) && (
            <div className='unit-drawer__actionbar'>
              <div className='unit-drawer__actionbar-start'>
                {canHideDeactivate && (
                  <>
                    {data.status === 'active' && (
                      <Button
                        variant='ghost'
                        iconStart='circle-slash'
                        onClick={() => setConfirmAction('deactivate')}
                      >
                        {t('unit_deactivate')}
                      </Button>
                    )}
                    {(data.status === 'deactivated' || data.status === 'hidden') && (
                      <Button
                        variant='ghost'
                        iconStart='circle-check'
                        onClick={() => setConfirmAction('activate')}
                        disabled={data.status !== 'deactivated'}
                      >
                        {t('unit_activate')}
                      </Button>
                    )}
                    {(data.status === 'active' || data.status === 'deactivated') && (
                      <Button
                        variant='ghost'
                        iconStart='eye-off'
                        onClick={() => setConfirmAction('hide')}
                        disabled={data.status !== 'deactivated'}
                      >
                        {t('unit_hide')}
                      </Button>
                    )}
                    {data.status === 'hidden' && (
                      <Button
                        variant='ghost'
                        iconStart='eye'
                        onClick={() => setConfirmAction('unhide')}
                      >
                        {t('unit_unhide')}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <div className='unit-drawer__actionbar-end'>
                {canEdit &&
                  (dirty ? (
                    <>
                      <Button
                        variant='secondary'
                        onClick={() => {
                          setDraft(initial);
                          setDraftPeople(data.people);
                          setSubmitted(false);
                        }}
                        disabled={saving}
                      >
                        {t('unit_discard')}
                      </Button>
                      <Button iconStart='check' onClick={save} loading={saving}>
                        {t('unit_save')}
                      </Button>
                    </>
                  ) : (
                    <span className='unit-drawer__saved'>
                      <Icon name='check' size={15} />
                      {t('unit_saved_badge')}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {confirmAction && (
            <ConfirmModal
              open
              onClose={() => setConfirmAction(null)}
              onConfirm={runStatus}
              tone={ACTION_TONE[confirmAction]}
              icon={ACTION_ICON[confirmAction]}
              title={t(`unit_confirm_${confirmAction}_title`, { name: data.name })}
              description={t(`unit_confirm_${confirmAction}_body`, {
                sub: subUnits,
                people: data.peopleCount,
              })}
              confirmLabel={t(`unit_${confirmAction}`)}
              loading={statusSaving}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default UnitDetailsDrawer;
